import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, X } from "lucide-react";

const fmt = (iso) => {
  if (!iso) return "—";
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split("-");
  return y && m && d ? `${d}-${m}-${y}` : s;
};

export default function ContratoCalendarDialog({ mid, cid, contratoName, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterCargo, setFilterCargo] = useState("");
  const today = new Date();
  const [year, setYear] = useState(today.getUTCFullYear());
  const [month, setMonth] = useState(today.getUTCMonth() + 1);

  const load = async () => {
    setLoading(true);
    try {
      const mm = `${year}-${String(month).padStart(2, "0")}`;
      const { data } = await api.get(
        `/mandantes/${mid}/contratos/${cid}/calendario`,
        { params: { month: mm } }
      );
      setData(data);
    } catch (e) { toast.error("No se pudo cargar el calendario"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  const next = () => { if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1); };
  const prev = () => { if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1); };

  const monthName = (() => {
    const n = new Date(year, month - 1, 1).toLocaleString("es-CL", { month: "long", year: "numeric" });
    return n.charAt(0).toUpperCase() + n.slice(1);
  })();
  const dayLabel = (iso) => iso.slice(8, 10);
  const dayName = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("es-CL", { weekday: "short" }).slice(0, 2);

  const cellClass = (s) => {
    if (s === "trabajo") return "bg-emerald-500 text-white";
    if (s === "descanso") return "bg-slate-200 text-slate-500";
    if (s === "antes") return "bg-amber-50 text-amber-500 border border-dashed border-amber-200";
    return "bg-slate-50 text-slate-300";
  };
  const cellLetter = (s) => s === "trabajo" ? "T" : s === "descanso" ? "D" : s === "antes" ? "·" : "—";

  const cargosDisponibles = Array.from(new Set((data?.workers || []).map((w) => (w.position || "").trim()).filter(Boolean))).sort();
  const filteredWorkers = (data?.workers || []).filter((w) =>
    !filterCargo || (w.position || "").trim().toUpperCase() === filterCargo.toUpperCase()
  );

  // Resumen por día: cuántos T hoy
  const totalWorkTodayPerDay = (data?.days || []).map((_, i) =>
    filteredWorkers.filter((w) => w.dias[i] === "trabajo").length
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-[95vw] max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h3 className="font-display font-semibold text-slate-800 flex items-center gap-2">
              <CalendarIcon size={18} className="text-indigo-600" />
              Calendario del contrato · {contratoName}
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">Vista mensual de todos los trabajadores. Las jornadas con ciclo pintan T (trabajo) / D (descanso).</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5">
              <label className="text-xs text-slate-500 font-medium">Cargo:</label>
              <select data-testid="cal-filter-cargo" value={filterCargo}
                onChange={(e) => setFilterCargo(e.target.value)}
                className="h-8 px-2 rounded-md border border-slate-200 text-xs bg-white">
                <option value="">Todos</option>
                {cargosDisponibles.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <button onClick={prev} data-testid="cal-prev" className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-slate-200 hover:bg-slate-50"><ChevronLeft size={16} /></button>
            <span className="min-w-[150px] text-center text-sm font-semibold" data-testid="cal-month-label">{monthName}</span>
            <button onClick={next} data-testid="cal-next" className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-slate-200 hover:bg-slate-50"><ChevronRight size={16} /></button>
            <button onClick={onClose} data-testid="close-contrato-calendar" className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-slate-100 text-slate-500 ml-2"><X size={16} /></button>
          </div>
        </div>

        <div className="overflow-auto flex-1">
          {loading || !data ? (
            <div className="p-10 text-sm text-slate-400">Cargando…</div>
          ) : (
            <div className="w-full">
              <table className="text-xs w-full table-fixed" data-testid="contrato-calendar">
                <colgroup>
                  <col style={{ width: 220 }} />
                  <col style={{ width: 120 }} />
                  <col style={{ width: 120 }} />
                  {data.days.map((d) => <col key={d} />)}
                </colgroup>
                <thead>
                  <tr>
                    <th className="sticky left-0 bg-slate-50 z-10 px-3 py-2 text-left font-semibold text-slate-600 border-b border-r border-slate-200">Trabajador</th>
                    <th className="sticky left-[220px] bg-slate-50 z-10 px-2 py-2 text-left font-semibold text-slate-600 border-b border-r border-slate-200">Cargo</th>
                    <th className="sticky left-[340px] bg-slate-50 z-10 px-2 py-2 text-left font-semibold text-slate-600 border-b border-r border-slate-200">Jornada</th>
                    {data.days.map((d) => (
                      <th key={d} className="px-0 py-2 text-center font-medium text-slate-500 border-b border-slate-100 bg-slate-50">
                        <div className="text-[10px] text-slate-400 uppercase">{dayName(d)}</div>
                        <div>{dayLabel(d)}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredWorkers.length === 0 && (
                    <tr>
                      <td colSpan={data.days.length + 3} className="p-6 text-sm text-slate-400 italic text-center">
                        {filterCargo ? `Sin trabajadores con cargo "${filterCargo}".` : "Sin trabajadores en este contrato."}
                      </td>
                    </tr>
                  )}
                  {filteredWorkers.map((w) => (
                    <tr key={w.worker_id} data-testid={`cal-row-${w.worker_id}`} className="hover:bg-slate-50/40">
                      <td className="sticky left-0 bg-white z-10 px-3 py-2 border-b border-r border-slate-100">
                        <div className="font-medium text-slate-800 truncate">{w.nombre}</div>
                        <div className="text-[10px] text-slate-400">{w.rut || "—"}</div>
                      </td>
                      <td className="sticky left-[220px] bg-white z-10 px-2 py-2 border-b border-r border-slate-100 text-slate-600 text-[11px] truncate">
                        {w.position || "—"}
                      </td>
                      <td className="sticky left-[340px] bg-white z-10 px-2 py-2 border-b border-r border-slate-100 whitespace-nowrap">
                        {w.jornada_res ? (
                          <div>
                            <div className="text-[11px] font-semibold text-indigo-700">{w.ciclo_label}</div>
                            <div className="text-[10px] text-slate-400">Res. {w.jornada_res}</div>
                          </div>
                        ) : (
                          <span className="text-[10px] text-slate-400 italic">Sin jornada</span>
                        )}
                      </td>
                      {w.dias.map((s, i) => (
                        <td key={i} className="px-0 py-1 border-b border-slate-100">
                          <div className={`h-7 w-full rounded flex items-center justify-center text-[10px] font-semibold ${cellClass(s)}`}
                            title={`${data.days[i]} · ${s}`}>
                            {cellLetter(s)}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                  {filteredWorkers.length > 0 && (
                    <tr className="bg-slate-50">
                      <td className="sticky left-0 bg-slate-50 z-10 px-3 py-2 border-r border-slate-200 font-semibold text-slate-700" colSpan={3}>
                        Dotación en turno {filterCargo ? `(${filterCargo})` : ""}
                      </td>
                      {totalWorkTodayPerDay.map((n, i) => (
                        <td key={i} className="px-0 py-1 border-t border-slate-200 text-center text-[11px] font-semibold text-emerald-700">
                          {n > 0 ? n : "—"}
                        </td>
                      ))}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="border-t border-slate-100 px-5 py-3 flex items-center gap-4 bg-slate-50 flex-wrap">
          <div className="flex items-center gap-1.5 text-xs text-slate-600"><span className="inline-block h-3 w-3 rounded bg-emerald-500" /> Trabajo (T)</div>
          <div className="flex items-center gap-1.5 text-xs text-slate-600"><span className="inline-block h-3 w-3 rounded bg-slate-200" /> Descanso (D)</div>
          <div className="flex items-center gap-1.5 text-xs text-slate-600"><span className="inline-block h-3 w-3 rounded bg-amber-50 border border-dashed border-amber-200" /> Antes del ingreso al ciclo</div>
          <div className="flex items-center gap-1.5 text-xs text-slate-600"><span className="inline-block h-3 w-3 rounded bg-white border border-slate-300" /> Sin jornada / sin ciclo</div>
        </div>
      </div>
    </div>
  );
}
